import { z } from "zod";

import { branded } from "@/lib/helpers/branded-helper.ts";

export enum ESiteWorkingConditionModulationStatus {
  UNKNOWN = "UNKNOWN",
  NONE = "NONE",
  NOT_TESTED = "NOT_TESTED",
  KO_DETECTED = "KO_DETECTED",
  SUCCESS = "SUCCESS",
}

export enum ESiteWorkingConditionAcquisitionStatus {
  UNKNOWN = "UNKNOWN",
  BAD = "BAD",
  MEDIUM = "MEDIUM",
  GOOD = "GOOD",
}

export const siteWorkingConditionDtoSchema = z.object({
  // TODO : branded.localeDateTime
  modulationEndDate: z.coerce.date().nullable(),
  linkModulationPerformance: z.object({
    modulationNumber: z.number().nullable(),
    modulationPerformancePercentage: z.number().nullable(),
  }),
  // TODO : branded.localeDateTime
  lastPointDate: z.coerce.date().nullable(),
  csModulatorId: branded.modulatorCsId,
  csLinkId: branded.modulatorLinkCsId,
  acqRate: z.object({
    raw: z.number(),
    interpreted: z.number(),
    isECS: z.boolean(),
  }),
});

export type TSiteWorkingConditionDto = z.infer<typeof siteWorkingConditionDtoSchema>;

export function getSiteWorkingConditionAcquisitionRate(swc?: Array<TSiteWorkingConditionDto>) {
  if (!swc || !swc.length) {
    return 0;
  }

  const sum = swc.map((mwc) => mwc.acqRate?.interpreted ?? 0).reduce((prev, curr) => prev + curr, 0);

  return Math.ceil(sum / swc.length);
}

export function getSiteWorkingConditionModulationRate(swc?: Array<TSiteWorkingConditionDto>) {
  if (!swc || !swc.length) {
    return 0;
  }

  const sum = swc
    .map((mwc) => mwc.linkModulationPerformance?.modulationPerformancePercentage ?? 0)
    .reduce((prev, curr) => prev + curr, 0);

  return Math.ceil(sum / swc.length);
}

export function getSiteWorkingConditionModulationStatus(swc?: Array<TSiteWorkingConditionDto>) {
  if (!swc || !swc.length) {
    return ESiteWorkingConditionModulationStatus.UNKNOWN;
  }

  const modulations = swc.filter(
    (mwc) => mwc.linkModulationPerformance === null || !mwc.linkModulationPerformance.modulationNumber,
  );

  if (modulations.length) {
    return ESiteWorkingConditionModulationStatus.NONE;
  }

  const testedModulators = swc.filter(
    (mwc) => mwc.linkModulationPerformance === null || (mwc.linkModulationPerformance?.modulationNumber ?? 0) <= 5,
  );

  if (testedModulators.length) {
    return ESiteWorkingConditionModulationStatus.NOT_TESTED;
  }

  const koModulators = swc.filter(
    (mwc) =>
      mwc.linkModulationPerformance === null ||
      (mwc.linkModulationPerformance?.modulationNumber ?? 0) <= 5 ||
      (mwc.linkModulationPerformance?.modulationPerformancePercentage ?? 0) <= 80,
  );

  if (koModulators.length) {
    return ESiteWorkingConditionModulationStatus.KO_DETECTED;
  }

  return ESiteWorkingConditionModulationStatus.SUCCESS;
}

export function getSiteWorkingConditionAcquisitionStatus(swc?: Array<TSiteWorkingConditionDto>) {
  if (!swc || !swc.length) {
    return ESiteWorkingConditionAcquisitionStatus.UNKNOWN;
  }

  const isBad = swc.every((mwc) => mwc.acqRate === null || mwc.acqRate.interpreted === 0);

  if (isBad) {
    return ESiteWorkingConditionAcquisitionStatus.BAD;
  }

  const isMedium = swc.some((mwc) => !mwc.acqRate?.isECS && (mwc.acqRate?.interpreted ?? 0) < 80);

  if (isMedium) {
    return ESiteWorkingConditionAcquisitionStatus.MEDIUM;
  }

  return ESiteWorkingConditionAcquisitionStatus.GOOD;
}
